// ═══════════════════════════════════════════════════════════════
// V·IANet Flow — DocumentNode: 90×38 with wave bottom
// M2: Fuente uniforme T.font.nodeTitle, sin descripción
// M4: Edición inline con doble clic (data.editing)
// M8: NodeResizer controlado por data.resizeEnabled
// ═══════════════════════════════════════════════════════════════

import { memo, useState, useCallback, useRef, useEffect } from 'react';
import { Handle, Position, NodeResizer, type NodeProps, useReactFlow } from '@xyflow/react';
import { FLOW_TOKENS as T } from '../../shared/flowTokens';
import type { DocumentNodeData, FlowNodeData } from '../../types/flowchart.types';

const W = T.node.width;
const H = T.node.height;
const SW = T.shape.strokeWidth;

function DocumentNodeComponent({ id, data, selected }: NodeProps) {
  const d = data as unknown as DocumentNodeData;
  const { updateNodeData } = useReactFlow();
  const [editValue, setEditValue] = useState(d.label);
  const inputRef = useRef<HTMLInputElement>(null);
  const editing = !!(d as any).editing;
  const stroke = selected ? T.color.nodeStrokeSelected : T.color.nodeStroke;
  const strokeW = selected ? T.shape.strokeWidthSelected : SW;

  useEffect(() => {
    if (editing) {
      setEditValue(d.label);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [editing, d.label]);

  const saveEdit = useCallback(() => {
    updateNodeData(id, { label: editValue, editing: false } as Partial<FlowNodeData>);
  }, [id, editValue, updateNodeData]);

  const cancelEdit = useCallback(() => {
    updateNodeData(id, { editing: false } as Partial<FlowNodeData>);
  }, [id, updateNodeData]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      e.stopPropagation(); // Prevent React Flow from capturing keys (Backspace, Delete, etc.)
      if (e.key === 'Enter') saveEdit();
      if (e.key === 'Escape') cancelEdit();
    },
    [saveEdit, cancelEdit],
  );

  const wavePath = `
    M ${SW / 2},${T.shape.borderRadius}
    Q ${SW / 2},${SW / 2} ${T.shape.borderRadius},${SW / 2}
    L ${W - T.shape.borderRadius},${SW / 2}
    Q ${W - SW / 2},${SW / 2} ${W - SW / 2},${T.shape.borderRadius}
    L ${W - SW / 2},${H - 6}
    C ${W * 0.75},${H + 1} ${W * 0.5},${H - 7} ${W * 0.25},${H}
    C ${W * 0.1},${H + 2} ${SW / 2},${H - 4} ${SW / 2},${H - 6}
    Z
  `;

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <NodeResizer
        minWidth={60}
        minHeight={30}
        isVisible={!!selected && !!(d as any).resizeEnabled}
        lineClassName="node-resizer-line"
        handleClassName="node-resizer-handle"
      />
      <Handle
        type="target"
        position={Position.Top}
        id="top-t"
        style={{ background: T.color.nodeStroke, width: 4, height: 4, border: 'none' }}
      />
      <Handle
        type="source"
        position={Position.Top}
        id="top-s"
        style={{ background: 'transparent', width: 10, height: 10, border: 'none' }}
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id="bottom-t"
        style={{ background: T.color.nodeStroke, width: 4, height: 4, border: 'none' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom-s"
        style={{ background: 'transparent', width: 10, height: 10, border: 'none' }}
      />
      <Handle
        type="target"
        position={Position.Right}
        id="right-t"
        style={{ background: T.color.nodeStroke, width: 4, height: 4, border: 'none' }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right-s"
        style={{ background: 'transparent', width: 10, height: 10, border: 'none' }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left-t"
        style={{ background: T.color.nodeStroke, width: 4, height: 4, border: 'none' }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left-s"
        style={{ background: 'transparent', width: 10, height: 10, border: 'none' }}
      />

      <svg width="100%" height="100%" preserveAspectRatio="none" viewBox={`0 0 ${W} ${H}`}>
        <path d={wavePath} fill={T.color.nodeFill} stroke={stroke} strokeWidth={strokeW} />
      </svg>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: T.font.family,
          pointerEvents: editing ? 'auto' : 'none',
          padding: '2px 4px 6px',
        }}
      >
        {editing ? (
          <input
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={saveEdit}
            style={{
              fontSize: T.font.nodeTitle.size,
              fontWeight: T.font.nodeTitle.weight,
              fontFamily: T.font.family,
              color: T.color.textPrimary,
              textAlign: 'center',
              border: 'none',
              outline: 'none',
              background: 'transparent',
              width: '100%',
              padding: 0,
            }}
          />
        ) : (
          <span
            style={{
              fontSize: T.font.nodeTitle.size,
              fontWeight: T.font.nodeTitle.weight,
              color: T.color.textPrimary,
              textAlign: 'center',
              lineHeight: 1.2,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
            }}
            title={d.label}
          >
            {d.label}
          </span>
        )}
      </div>
    </div>
  );
}

export const DocumentNode = memo(DocumentNodeComponent);
